'use client';

import { FilterIcon } from '@/_assets/icon-components';
import { getBrandSlug, resolveBrandId } from '@/_lib/brand-href';
import { BRAND_NAMES, FEATURED_BRANDS } from '@/_utils/Constants';
import {
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverRoot,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  CustomIndicator,
  RadioCardItem,
  RadioCardRoot,
} from '@/components/ui/radio-card';
import {
  Button,
  Flex,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';

const BrandFilter = () => {
  const t = useTranslations();
  const { open, onClose, onToggle } = useDisclosure();

  const [selectedShipper, setSelectedShipper] = useQueryState('shipper', {
    defaultValue: '',
  });

  const selectedShipperId = resolveBrandId(selectedShipper);

  const [tempSelectedShipper, setTempSelectedShipper] =
    useState<string>(selectedShipperId);

  useEffect(() => {
    if (open) {
      setTempSelectedShipper(selectedShipperId);
    }
  }, [open, selectedShipperId]);

  const uniqueShippers = useMemo(() => {
    return Object.entries(BRAND_NAMES)
      // Hide Yoko Pizza
      .filter(([id]) => id !== FEATURED_BRANDS.yokopizza)
      .map(([key, value]) => ({
        id: key,
        name: value,
        slug: getBrandSlug(key),
      }));
  }, []);

  const handleShipperChange = (value: string | null) => {
    setTempSelectedShipper(value || '');
  };

  const handleApplyFilter = () => {
    setSelectedShipper(
      tempSelectedShipper ? getBrandSlug(tempSelectedShipper) : '',
    );
    onClose();
  };

  const handleResetFilter = () => {
    setTempSelectedShipper('');
    setSelectedShipper('');
  };

  return (
    <PopoverRoot
      open={open}
      onOpenChange={onToggle}
      positioning={{ placement: 'bottom-end' }}
    >
      <PopoverTrigger asChild>
        <Button
          h={{ base: '32px', lg: '56px' }}
          rounded={{ base: 12, lg: 16 }}
          color={selectedShipper ? 'primary' : 'transparent'}
          variant={{ base: 'ghost', lg: 'solid' }}
          bg={{ base: 'transparent', lg: 'white' }}
          size={{ base: 'xs', lg: '2xl' }}
          p={{ lg: '4' }}
        >
          <HStack gap={2}>
            <FilterIcon
              color={
                selectedShipper
                  ? 'var(--chakra-colors-primary)'
                  : 'var(--chakra-colors-text)'
              }
            />
            <Text
              display={{ base: 'none', lg: 'block' }}
              color={selectedShipper ? 'primary' : 'text'}
            >
              {t('filters')}
            </Text>
          </HStack>
        </Button>
      </PopoverTrigger>
      <PopoverContent w='300px' bg='white' p={0}>
        <PopoverHeader p={2} px={4}>
          <Flex justifyContent='space-between' alignItems='center'>
            <PopoverTitle fontWeight={700}>
              {t('only_in_home_page.filter')}
            </PopoverTitle>
            <Button
              px={2}
              variant='plain'
              color='primary'
              onClick={handleResetFilter}
              size='sm'
            >
              {t('only_in_home_page.reset_filter')}
            </Button>
          </Flex>
        </PopoverHeader>
        <PopoverBody p={2} pt={0}>
          <RadioCardRoot
            value={tempSelectedShipper || ''}
            onValueChange={(e) => handleShipperChange(e.value ?? null)}
          >
            <VStack gap={0} align='start'>
              {uniqueShippers.map((shipper) => (
                <RadioCardItem
                  p={2}
                  py={0}
                  my={0}
                  h={'full'}
                  w={'full'}
                  border='none'
                  shadow='none'
                  cursor='pointer'
                  indicator={false}
                  key={shipper.id}
                  value={shipper.id}
                >
                  <Flex gap={4} color={'text'} align='center'>
                    <label htmlFor={shipper.id}>{shipper.name}</label>
                  </Flex>
                  <CustomIndicator
                    w={5}
                    h={5}
                    active={
                      tempSelectedShipper === shipper.id ||
                      tempSelectedShipper === shipper.slug
                    }
                  />
                </RadioCardItem>
              ))}
            </VStack>
          </RadioCardRoot>
        </PopoverBody>
        <PopoverFooter p={4} pt={0}>
          <Button
            w='full'
            bg='primary'
            rounded={8}
            color='white'
            onClick={handleApplyFilter}
            _hover={{ bg: 'primary.hover' }}
          >
            {t('to_filter')}
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </PopoverRoot>
  );
};

export default BrandFilter;
